'use client';

import { useEffect, useRef, useState } from 'react';

interface CustomScrollbarProps {
    introActive?: boolean;
}

export default function CustomScrollbar({ introActive = false }: CustomScrollbarProps) {
    const [isVisible, setIsVisible] = useState(false);
    const trackRef = useRef<HTMLDivElement>(null);
    const thumbRef = useRef<HTMLDivElement>(null);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        let revealFrameOne: number | null = null;
        let revealFrameTwo: number | null = null;

        const updateThumb = () => {
            rafRef.current = null;
            const track = trackRef.current;
            const thumb = thumbRef.current;
            if (!track || !thumb) return;

            const viewportHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollRange = Math.max(0, documentHeight - viewportHeight);
            const progress = scrollRange > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollRange)) : 0;
            const trackHeight = track.clientHeight;
            const thumbHeight = Math.max(48, Math.min(trackHeight, trackHeight * (viewportHeight / documentHeight)));
            const travel = Math.max(0, trackHeight - thumbHeight);

            thumb.style.height = `${thumbHeight}px`;
            thumb.style.transform = `translate3d(0, ${Math.round(progress * travel)}px, 0)`;
        };

        const scheduleUpdate = () => {
            if (rafRef.current === null) {
                rafRef.current = window.requestAnimationFrame(updateThumb);
            }
        };

        updateThumb();
        const contentResizeObserver = new ResizeObserver(scheduleUpdate);
        contentResizeObserver.observe(document.body);
        contentResizeObserver.observe(document.documentElement);

        let isDisposed = false;
        void document.fonts?.ready.then(() => {
            if (!isDisposed) scheduleUpdate();
        });

        if (introActive) {
            setIsVisible(false);
        } else {
            revealFrameOne = window.requestAnimationFrame(() => {
                revealFrameTwo = window.requestAnimationFrame(() => {
                    updateThumb();
                    setIsVisible(true);
                });
            });
        }

        window.addEventListener('scroll', scheduleUpdate, { passive: true });
        window.addEventListener('resize', scheduleUpdate);
        window.addEventListener('load', scheduleUpdate);

        return () => {
            isDisposed = true;
            contentResizeObserver.disconnect();
            window.removeEventListener('scroll', scheduleUpdate);
            window.removeEventListener('resize', scheduleUpdate);
            window.removeEventListener('load', scheduleUpdate);
            if (revealFrameOne !== null) window.cancelAnimationFrame(revealFrameOne);
            if (revealFrameTwo !== null) window.cancelAnimationFrame(revealFrameTwo);
            if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
        };
    }, [introActive]);

    return (
        <div
            aria-hidden
            data-custom-scrollbar
            className={`pointer-events-none fixed bottom-2 right-1 top-2 z-[60] w-1.5 transform-gpu transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{ contain: 'strict' }}
        >
            <div ref={trackRef} className="relative h-full w-full overflow-hidden rounded-full border border-white/[0.05] bg-black/25">
                <div
                    ref={thumbRef}
                    data-scrollbar-thumb
                    className="absolute left-0 top-0 w-full transform-gpu rounded-full bg-gradient-to-b from-cyan-400 via-violet-500 to-fuchsia-500 shadow-[0_0_10px_rgba(56,220,255,0.28)]"
                    style={{ minHeight: 48, willChange: 'transform' }}
                />
            </div>
        </div>
    );
}
